import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Slider from '@material-ui/core/Slider';
import Divider from '@material-ui/core/Divider';
import { createReceiptPDF, receiptPrinterPageSizeList, receiptPrinterDefaultFields } from 'utilities/pdf';

const fieldLabelMapping = {
  date: '日期時間',
  orderShortId: '取餐編號',
  deliveryGroupId: '送餐群組',
  name: '姓名',
  tags: '飲食標籤',
  noteForMeal: '餐點備註',
  meals: '餐點',
  mealNotes: '餐點註記',
  custom: '自訂',
};

const ReceiptPrinterSettingsWidget = (props) => {
  const { formData, onChange, schema } = props;
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const [value, setValue] = useState();

  useEffect(() => {
    const fields = receiptPrinterDefaultFields.map((defaultField) => {
      const inField = formData?.fields?.find((f) => f.id === defaultField.id) || {};
      return {
        ...defaultField,
        ...inField,
      };
    });
    const newValue = {};
    newValue.fields = fields;
    newValue.width = formData?.width || receiptPrinterPageSizeList[0].width;
    newValue.height = formData?.height || receiptPrinterPageSizeList[0].height;

    setValue(newValue);
  }, [formData]);

  const updateField = (fieldId, updates) => {
    const newFields = value.fields.map((field) =>
      field.id === fieldId ? { ...field, ...updates } : field,
    );
    onChange({ ...value, fields: newFields });
  };

  const toggleFieldVisibility = (fieldId) => {
    const field = value.fields.find((f) => f.id === fieldId);
    if (field) {
      updateField(fieldId, { visible: !field.visible });
    }
  };

  const generatePreview = async () => {
    setIsGeneratingPreview(true);
    try {
      const previewData = {
        id: 'b31bb3a8-1c4f-494a-bcdb-9faefadea509',
        elder: {
          name: '張小明',
          tags: {
            items: [
              { tagId: 'tag1' },
              { tagId: 'tag2' },
            ],
          },
        },
        mealItems: [
          { name: '排骨飯', quantity: 1, note: '切碎一點' },
        ],
        deliveryBy: new Date().toISOString(),
        mealSlot: 'lunch',
        deliveryGroupId: '1234567890',
        noteForMeal: '吃素，不吃麵',
      };
      const pdfBytes = await createReceiptPDF([previewData], value, [
        { id: 'tag1', label: '1/2飯' },
        { id: 'tag2', label: '湯' },
      ], [
        { id: '1234567890', name: '送餐群組1' },
      ]);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
    } catch (error) {
      console.error('預覽失敗:', error);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // 即時預覽
  useEffect(() => {
    if (!value) return;

    const timer = setTimeout(() => {
      generatePreview();
    }, 1000);

    return () => {
      clearTimeout(timer);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [value]);

  if (!value) return null;

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" width="100%">
      <Typography variant="h5" color="black">
        {schema.title || '出單機設定'}
      </Typography>
      <Box
        sx={{
          width: '100%',
          height: '1px',
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
          marginBottom: 16,
        }}
      />

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <Paper elevation={2} style={{ padding: 16, maxHeight: '600px', overflowY: 'auto' }}>
            <Typography variant="h6" gutterBottom>
              欄位設定
            </Typography>
            {value.fields.map((field) => (
              <Box key={field.id} mb={2} p={2} border={1} borderColor="grey.300" borderRadius={1}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">{fieldLabelMapping[field.id]}</Typography>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={field.visible}
                      onChange={() => toggleFieldVisibility(field.id)}
                      size="small"
                    />
                  }
                  label="顯示"
                />

                <Box mt={1}>
                  <Typography variant="caption">字體大小: {field.fontSize}</Typography>
                  <Slider
                    value={field.fontSize}
                    onChange={(_, newValue) => updateField(field.id, { fontSize: newValue })}
                    min={4}
                    max={20}
                    step={1}
                    size="small"
                  />
                </Box>

                {field.allowBold && (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.bold}
                        onChange={(e) => updateField(field.id, { bold: e.target.checked })}
                        size="small"
                      />
                    }
                    label="粗體"
                  />
                )}

                <Box mt={1}>
                  <Typography variant="caption">位置 X: {Math.round(field.x)}  (0代表最左邊)</Typography>
                  <Slider
                    value={field.x}
                    onChange={(_, newValue) => updateField(field.id, { x: newValue })}
                    min={0}
                    max={value.width}
                    step={1}
                    size="small"
                  />
                </Box>

                <Box mt={1}>
                  <Typography variant="caption">位置 Y: {Math.round(field.y)}  (0代表最上面)</Typography>
                  <Slider
                    value={field.y}
                    onChange={(_, newValue) => updateField(field.id, { y: newValue })}
                    min={0}
                    max={value.height}
                    step={1}
                    size="small"
                  />
                </Box>

                {field.id === 'custom' && (
                  <Box mt={1}>
                    <TextField
                      label="內容"
                      value={field.content}
                      onChange={(e) => updateField(field.id, { content: e.target.value })}
                      fullWidth
                      size="small"
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" gutterBottom>
            紙張設定
          </Typography>
          <TextField
            select
            label="紙張型號"
            value={`${value.width}x${value.height}`}
            onChange={(e) => {
              const [width, height] = e.target.value.split('x').map(Number);
              onChange({ ...value, width, height });
            }}
            fullWidth
            size="small"
          >
            {receiptPrinterPageSizeList.map((option, index) => (
              <option key={`${option.models}-${index}`} value={`${option.width}x${option.height}`}>
                {option.models}（{option.width}x{option.height}mm）
              </option>
            ))}
          </TextField>
          <Divider style={{ margin: '16px 0' }} />
          <Paper elevation={2} style={{ padding: 16 }}>
            <Typography variant="h6" gutterBottom>
              PDF 預覽
            </Typography>
            {isGeneratingPreview && (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                <Typography>正在生成預覽...</Typography>
              </Box>
            )}
            {previewUrl && !isGeneratingPreview && (
              <iframe
                src={previewUrl}
                style={{
                  width: '100%',
                  height: '400px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
                title="PDF預覽"
              />
            )}
            <Box mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={generatePreview}
                disabled={isGeneratingPreview}
                fullWidth
              >
                重新生成預覽
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

ReceiptPrinterSettingsWidget.propTypes = {
  formData: PropTypes.object,
  onChange: PropTypes.func,
  schema: PropTypes.object,
};

export default ReceiptPrinterSettingsWidget;
